import { Check, Hand, Minus, X } from "lucide-react";

import { cn } from "@/lib/utils";

/** Step status → the node's fill/ink pair. Unknown statuses fall back to the
 *  pending look so a new server status never renders as a blank hole. */
const NODE_STYLES: Record<string, string> = {
  done: "bg-success-dim text-success",
  failed: "bg-destructive-dim text-destructive",
  skipped: "bg-surface-elevated text-muted-foreground",
  running: "bg-primary-dim text-primary",
  awaiting_approval: "bg-warning-dim text-warning",
  awaiting_human: "bg-warning-dim text-warning",
  pending: "border border-border bg-card text-muted-foreground",
};

const NODE_LABELS: Record<string, string> = {
  done: "Done",
  failed: "Failed",
  skipped: "Skipped",
  running: "Running",
  awaiting_approval: "Awaiting approval",
  awaiting_human: "Awaiting you",
  pending: "Pending",
};

/** The glyph inside the node. Pending stays an empty circle — the absence of a
 *  mark is the "hasn't happened yet" signal. */
function nodeGlyph(status: string) {
  switch (status) {
    case "done":
      return <Check size={14} />;
    case "failed":
      return <X size={14} />;
    case "skipped":
      return <Minus size={14} />;
    case "running":
      return <span className="h-2 w-2 animate-pulse rounded-full bg-primary" />;
    case "awaiting_approval":
    case "awaiting_human":
      return <Hand size={13} />;
    default:
      return null;
  }
}

/**
 * A step's state as a timeline node: one disc whose fill, ink, and glyph all
 * encode the same status, so the timeline reads at a glance without a badge
 * per row. Sits on the timeline spine drawn by `RunStepTimeline`.
 */
export function StepStateIcon({ status }: { status: string }) {
  return (
    <span
      className={cn(
        "flex h-6 w-6 shrink-0 items-center justify-center rounded-full",
        NODE_STYLES[status] ?? NODE_STYLES.pending,
      )}
      title={NODE_LABELS[status] ?? status}
    >
      {nodeGlyph(status)}
      <span className="sr-only">{NODE_LABELS[status] ?? status}</span>
    </span>
  );
}
